import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Sparkles, ChevronDown, ChevronUp, Pencil, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_DEFAULT_SHELF = {
  Produce: 14, Dairy: 7, Meat: 3, Bakery: 4, Frozen: 90,
  Pantry: 180, Beverages: 30, Snacks: 30, Household: 730, "Personal Care": 730, Other: 30,
};

/**
 * CommonStaples — curated household items shown at the top of the Upcoming Buy tab.
 * Each staple has a one-tap "+" (adds today, auto-expiry) and a "Customize"
 * pencil that opens a dialog to tweak name/qty/unit/purchase-date/shelf-life
 * — useful when adding something that's already been in the pantry for a while.
 */
export default function CommonStaples({ vendors = [], onAdded, search = "" }) {
  const [staples, setStaples] = useState([]);
  const [units, setUnits] = useState({ units: [], defaults_by_category: {} });
  const [vendorPick, setVendorPick] = useState({});
  const [pending, setPending] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [customize, setCustomize] = useState(null); // {name, category, quantity, unit, purchase_date, shelf_life_days}

  const load = useCallback(async () => {
    try {
      const [s, u, vs] = await Promise.all([
        api.get("/common-staples"),
        api.get("/grocery/units"),
        api.get("/grocery/vendor-suggestions"),
      ]);
      setStaples(s.data.staples || []);
      setUnits(u.data || { units: [], defaults_by_category: {} });
      // Pre-fill the vendor picker for any staple we've bought before.
      const suggestions = vs.data?.suggestions || {};
      const initial = {};
      (s.data.staples || []).forEach((st) => {
        const hit = suggestions[st.name.toLowerCase()];
        if (hit?.vendor_id) initial[st.name] = hit.vendor_id;
      });
      if (Object.keys(initial).length) setVendorPick((p) => ({ ...initial, ...p }));
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const defaultUnit = (category) => units.defaults_by_category?.[category] || "each";

  const filtered = React.useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return staples;
    return staples.filter((s) =>
      s.name.toLowerCase().includes(q) ||
      s.category.toLowerCase().includes(q)
    );
  }, [staples, search]);

  const quickAdd = async (s) => {
    setPending((p) => ({ ...p, [s.name]: true }));
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post("/grocery/quick-add", {
        name: s.name, category: s.category, status: "upcoming",
        quantity: null, unit: defaultUnit(s.category),
        vendor_id: vendorPick[s.name] || null,
        purchase_date: today,
      });
      setStaples((prev) => prev.filter((x) => x.name !== s.name));
      toast.success(`${s.name} added`);
      onAdded?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPending((p) => { const c = { ...p }; delete c[s.name]; return c; }); }
  };

  const openCustomize = (s) => {
    setCustomize({
      name: s.name,
      category: s.category,
      quantity: "",
      unit: defaultUnit(s.category),
      vendor_id: vendorPick[s.name] || "",
      purchase_date: new Date().toISOString().slice(0, 10),
      shelf_life_days: String(CATEGORY_DEFAULT_SHELF[s.category] ?? 30),
    });
  };

  const saveCustomize = async () => {
    if (!customize) return;
    try {
      await api.post("/grocery/quick-add", {
        name: customize.name.trim() || "Item",
        category: customize.category,
        status: "upcoming",
        quantity: customize.quantity?.trim() || null,
        unit: customize.unit || defaultUnit(customize.category),
        vendor_id: customize.vendor_id || null,
        purchase_date: customize.purchase_date || null,
        shelf_life_days: customize.shelf_life_days ? parseInt(customize.shelf_life_days, 10) : null,
      });
      setStaples((prev) => prev.filter((x) => x.name !== customize.name));
      toast.success(`${customize.name} added`);
      onAdded?.();
      setCustomize(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (staples.length === 0) return null;
  if (search && filtered.length === 0) return null;

  const list = search ? filtered : (expanded ? staples : staples.slice(0, 8));

  return (
    <section className="warm-card p-5 mb-6 bg-hearth-sage/5 border-hearth-sage/20" data-testid="common-staples">
      <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-display text-xl text-hearth-ink inline-flex items-center gap-2">
            <Sparkles size={16} className="text-hearth-sageDark" /> Common staples
          </h3>
          <p className="caption mt-0.5">
            {search
              ? <>{filtered.length} match{filtered.length === 1 ? "" : "es"} for &ldquo;{search}&rdquo;</>
              : <>one-tap add — {staples.length} not on your list</>}
          </p>
        </div>
        {!search && staples.length > 8 && (
          <button
            data-testid="common-staples-toggle"
            onClick={() => setExpanded((x) => !x)}
            className="caption text-hearth-clayDark hover:text-hearth-clay inline-flex items-center gap-1"
          >
            {expanded ? <>Show less <ChevronUp size={12}/></> : <>Show all <ChevronDown size={12}/></>}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2">
        {list.map((s) => {
          const busy = pending[s.name];
          return (
            <div
              key={s.name}
              data-testid={`staple-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-center gap-2 p-2 rounded-2xl bg-hearth-paper border border-hearth-line"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm text-hearth-ink font-medium truncate">{s.name}</div>
                <div className="caption text-hearth-mute">{s.category}</div>
              </div>
              {vendors.length > 0 && (
                <Select
                  value={vendorPick[s.name] || "none"}
                  onValueChange={(v) => setVendorPick((p) => ({ ...p, [s.name]: v === "none" ? "" : v }))}
                >
                  <SelectTrigger
                    className="input-warm h-8 rounded-full text-xs w-[100px] shrink-0"
                    data-testid={`staple-vendor-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Any vendor</SelectItem>
                    {vendors.filter((v) => !(v.search_url_template || "").includes("{q}")).map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                data-testid={`staple-customize-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => openCustomize(s)}
                disabled={busy}
                variant="ghost"
                className="rounded-full h-8 w-8 p-0 text-hearth-mute hover:text-hearth-ink hover:bg-hearth-stone shrink-0"
                title="Customize — set purchase date, quantity, shelf life"
              >
                <Pencil size={12} />
              </Button>
              <Button
                data-testid={`staple-add-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => quickAdd(s)}
                disabled={busy}
                variant="ghost"
                className="rounded-full h-8 w-8 p-0 text-hearth-clayDark hover:text-hearth-clay hover:bg-hearth-clay/10 shrink-0"
                title={`Add ${s.name} — today, default shelf life`}
              >
                <Plus size={14} />
              </Button>
            </div>
          );
        })}
      </div>

      {/* Customize dialog */}
      <Dialog open={!!customize} onOpenChange={(o) => !o && setCustomize(null)}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold">
              Customize
            </DialogTitle>
            <DialogDescription className="text-hearth-mute text-sm">
              Tweak the name, quantity or purchase date. Shelf-life auto-fills from category — override it if this has been in the pantry for a while.
            </DialogDescription>
          </DialogHeader>
          {customize && (
            <div className="space-y-4" data-testid="staple-customize-dialog">
              <div>
                <Label className="caption text-hearth-mute">Item</Label>
                <Input
                  data-testid="staple-cust-name"
                  className="input-warm mt-1.5 h-11"
                  value={customize.name}
                  onChange={(e) => setCustomize({ ...customize, name: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="caption text-hearth-mute">Quantity (optional)</Label>
                  <Input
                    data-testid="staple-cust-qty"
                    className="input-warm mt-1.5 h-11"
                    value={customize.quantity}
                    onChange={(e) => setCustomize({ ...customize, quantity: e.target.value })}
                    placeholder="e.g. 500"
                  />
                </div>
                <div>
                  <Label className="caption text-hearth-mute">Unit</Label>
                  <Select value={customize.unit} onValueChange={(v) => setCustomize({ ...customize, unit: v })}>
                    <SelectTrigger className="input-warm mt-1.5 h-11 rounded-xl" data-testid="staple-cust-unit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(units.units || []).map((u) => (
                        <SelectItem key={u} value={u}>{u}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="caption text-hearth-mute inline-flex items-center gap-1">
                    <CalendarClock size={11} /> Bought on
                  </Label>
                  <Input
                    data-testid="staple-cust-date"
                    type="date"
                    className="input-warm mt-1.5 h-11"
                    value={customize.purchase_date}
                    onChange={(e) => setCustomize({ ...customize, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="caption text-hearth-mute">Shelf life (days)</Label>
                  <Input
                    data-testid="staple-cust-shelf"
                    type="number"
                    min="1"
                    className="input-warm mt-1.5 h-11 font-mono"
                    value={customize.shelf_life_days}
                    onChange={(e) => setCustomize({ ...customize, shelf_life_days: e.target.value })}
                  />
                </div>
              </div>
              {vendors.length > 0 && (
                <div>
                  <Label className="caption text-hearth-mute">Vendor (optional)</Label>
                  <Select
                    value={customize.vendor_id || "none"}
                    onValueChange={(v) => setCustomize({ ...customize, vendor_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="input-warm mt-1.5 h-11 rounded-xl" data-testid="staple-cust-vendor">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Any vendor</SelectItem>
                      {vendors.filter((v) => !(v.search_url_template || "").includes("{q}")).map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCustomize(null)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="staple-cust-save"
              onClick={saveCustomize}
              className="rounded-full bg-hearth-clay hover:bg-hearth-clayDark text-white"
            >
              Add to list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
